/**
 * Centralised locale-restriction config and predicates.
 *
 * Two scopes:
 *   slugs      – gate entire pages (PageResolver redirects to / if not allowed)
 *   components – gate individual sections within a renderer (returns null)
 *
 * Keys are lowercase. Locale values are case-insensitive on comparison.
 */

export const localeRestrictions = {
  slugs: {
    "netflix-fan-club": ["en-US"],
    "netflix-ads-upfront-mx-2026": ["es-MX", "pt-BR"],
  } as Record<string, readonly string[]>,

  components: {} as Record<string, readonly string[]>,
};

const normalize = (value: string) => value.trim().toLowerCase();

export const isLocaleAllowed = (
  locale: string,
  allowedLocales?: readonly string[]
): boolean => {
  if (!allowedLocales || allowedLocales.length === 0) return true;
  const n = normalize(locale);
  return allowedLocales.some((l) => normalize(l) === n);
};

export const isSlugAllowedForLocale = (
  slug: string,
  locale: string
): boolean => {
  const allowed = localeRestrictions.slugs[normalize(slug)];
  return isLocaleAllowed(locale, allowed);
};

export const isComponentAllowedForLocale = (
  componentKey: string,
  locale: string
): boolean => {
  const allowed = localeRestrictions.components[componentKey];
  return isLocaleAllowed(locale, allowed);
};

const regionToMarketName = {
  US: "United States",
  AU: "Australia",
  BR: "Brazil",
  CA: "Canada",
  FR: "France",
  DE: "Germany",
  IT: "Italy",
  JP: "Japan",
  KR: "Korea",
  MX: "Mexico",
  ES: "Spain",
  GB: "United Kingdom",
};

export const isPageAllowedForLocale = (
  locale: string,
  allowedPageLocales: string[]
): boolean => {
  if (!allowedPageLocales || allowedPageLocales.length === 0) return false;
  const parts = locale.split("-");
  if (parts.length < 2) return false;
  const region = parts[parts.length - 1].toUpperCase();
  const marketName =
    regionToMarketName[region as keyof typeof regionToMarketName];
  return marketName ? allowedPageLocales.includes(marketName) : false;
};
